import { AccordionItem } from '@e3dc-react/shell/Elements/Accordion'
import { Typography } from '@e3dc-react/shell/Elements/Typography'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Grid } from '@mui/material'
import React, { useCallback, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { SettingsBox } from 'src/Elements/Data'
import SettingsFormContainer from 'src/Libs/form/components/SettingsFormContainer'
import { NumberField, Toggle } from 'src/Libs/inputs/react-hook-form'
import { UnitTypes } from 'src/Libs/types'
import useGridAndSystemProtectionMutation from '../hooks/use-grid-and-system-protection-mutation'
import useAreClientSoftwareVersionsInSync from '../hooks/use-are-client-software-versions-in-sync'
import useSyncFormWithValues from '../hooks/use-sync-form-with-values'
import { FUNCTION_NUMBER } from '../types/common'
import { LfsmOSettingsModel } from '../types/grid-and-system-protection.model'
import { getLfsmOSettingsSchema, LfsmOSettingsForm } from '../types/schemas'
import { getChangedFieldStyle, isFieldDisabled, canEditGridProtectionSettings } from '../utils'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'
import { useFarmContext } from 'src/Elements/Farm'
const NAME_SPACE = 'systemSettings.grid-and-system-protection-configuration.lfsm-o-settings'

type Props = {
  lfsmOSettingsModel: LfsmOSettingsModel
}

const LfsmOSettings: React.FC<Props> = ({ lfsmOSettingsModel }) => {
  const { t } = useCustomTranslation()
  const { isFarm, isFarmMasterSelected } = useFarmContext()
  const { hasInstallationWritingRights } = useInstallationAccessRightsContext()
  const { areInSync: areSoftwareVersionsInSync } = useAreClientSoftwareVersionsInSync()

  const canEdit = canEditGridProtectionSettings({ isFarm, isFarmMasterSelected, hasInstallationWritingRights, areSoftwareVersionsInSync })

  const defaultValues = useMemo(() => getDefaultValues(lfsmOSettingsModel), [lfsmOSettingsModel])
  const methods = useForm<LfsmOSettingsForm>({
    resolver: zodResolver(getLfsmOSettingsSchema(lfsmOSettingsModel)),
    defaultValues,
    mode: 'onBlur',
  })
  useSyncFormWithValues(methods.reset, defaultValues)

  const { isLoading, updateGridAndSystemProtection } = useGridAndSystemProtectionMutation()

  const onSubmit = useCallback(
    async (data: LfsmOSettingsForm): Promise<void> => {
      try {
        await updateGridAndSystemProtection({ lfsmOSettings: data }, FUNCTION_NUMBER.lfsmOSettings)
        methods.reset(data)
      } catch (error) {
        methods.reset()
      }
    },
    [methods, updateGridAndSystemProtection]
  )

  return (
    <FormProvider {...methods}>
      <Grid item xs={12}>
        <AccordionItem title={t(`${NAME_SPACE}.title`)}>
          <Box component="form" onSubmit={methods.handleSubmit(onSubmit)}>
            <SettingsFormContainer onReset={methods.reset} isLoading={isLoading}>
              <SettingsBox
                title={t(`${NAME_SPACE}.threshold-frequency.title`)}
                suffix={
                  <NumberField
                    name="thresholdFrequency"
                    label={t(`${NAME_SPACE}.threshold-frequency.label`)}
                    suffix={UnitTypes.frequency}
                    disabled={!canEdit || isFieldDisabled(lfsmOSettingsModel.thresholdFrequency)}
                    sx={getChangedFieldStyle(lfsmOSettingsModel.thresholdFrequency.changed)}
                  />
                }
              />
              <SettingsBox
                title={t(`${NAME_SPACE}.gradient.title`)}
                description={t(`${NAME_SPACE}.gradient.description`)}
                suffix={
                  <NumberField
                    name="gradient"
                    label={t(`${NAME_SPACE}.gradient.label`)}
                    suffix={UnitTypes.powerPerFrequency}
                    disabled // is calcualted from statics value
                    sx={getChangedFieldStyle(lfsmOSettingsModel.gradient.changed)}
                  />
                }
              />
              <SettingsBox
                title={t(`${NAME_SPACE}.return-gradient.title`)}
                suffix={
                  <NumberField
                    name="returnGradient"
                    label={t(`${NAME_SPACE}.return-gradient.label`)}
                    suffix={UnitTypes.powerPerFrequency}
                    disabled={!canEdit || isFieldDisabled(lfsmOSettingsModel.returnGradient)}
                    sx={getChangedFieldStyle(lfsmOSettingsModel.returnGradient.changed)}
                  />
                }
              />
              <SettingsBox
                title={t(`${NAME_SPACE}.statics.title`)}
                suffix={
                  <NumberField
                    name="statics"
                    label={t(`${NAME_SPACE}.statics.label`)}
                    suffix={UnitTypes.percentage}
                    disabled={!canEdit || isFieldDisabled(lfsmOSettingsModel.statics)}
                    sx={getChangedFieldStyle(lfsmOSettingsModel.statics.changed)}
                  />
                }
              />
              {lfsmOSettingsModel.activePowerReference && (
                <SettingsBox
                  title={t(`${NAME_SPACE}.activePowerReference.title`)}
                  suffix={
                    <Toggle
                      name="activePowerReference"
                      disabled={!canEdit || isFieldDisabled(lfsmOSettingsModel.activePowerReference)}
                      sx={getChangedFieldStyle(lfsmOSettingsModel.activePowerReference.changed)}
                      propsOnChange={checked => {
                        methods.setValue('activePowerReference', checked ? 1 : 0, { shouldDirty: true })
                      }}
                    />
                  }
                />
              )}
              <Typography variant="caption">{`${NAME_SPACE}.bottom-text`}</Typography>
            </SettingsFormContainer>
          </Box>
        </AccordionItem>
      </Grid>
    </FormProvider>
  )
}

export default LfsmOSettings

const getDefaultValues = (data: LfsmOSettingsModel): LfsmOSettingsForm => {
  return {
    thresholdFrequency: data.thresholdFrequency.value ? Number(data.thresholdFrequency.value?.toFixed(1)) : null,
    gradient: data.gradient.value,
    returnGradient: data.returnGradient.value,
    statics: data.statics.value,
    activePowerReference: data.activePowerReference?.value ?? null,
  }
}
